import QRCode from "qrcode";

export const LABEL_WIDTH_MM = 50;
export const LABEL_HEIGHT_MM = 30;
const DOTS_PER_MM = 8;
const MAX_QR_DOTS = 26 * DOTS_PER_MM;

export function prepareQRLabel(asset) {
  const trackingId = String(asset?.trackingId || "").trim();
  if (!trackingId)
    throw new Error(
      "This asset needs a tracking ID before its QR label can be printed.",
    );
  const url =
    "https://inv.vellaglobal.com/QRView/" + encodeURIComponent(trackingId);
  const modules = QRCode.create(url, { errorCorrectionLevel: "M" }).modules
    .size;
  // Four white modules on every side preserve the QR quiet zone.
  const dotsPerModule = Math.max(1, Math.floor(MAX_QR_DOTS / (modules + 8)));
  return {
    trackingId,
    url,
    qrMm: ((modules + 8) * dotsPerModule) / DOTS_PER_MM,
  };
}

export const labelPrintCSS = `
  @page { size: 50mm 30mm; margin: 0; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; font-family: Arial, sans-serif; color: #000; }
  body { background: #eef2f3; }
  .labels { width: 50mm; margin: 24px auto; }
  .label { position: relative; width: 50mm; height: 30mm; padding: 2mm; background: #fff; color: #000; display: flex; align-items: center; justify-content: center; gap: 1mm; overflow: hidden; break-inside: avoid; page-break-inside: avoid; break-after: auto; page-break-after: auto; }
  .label::after { content: ""; position: absolute; inset: 0.75mm; pointer-events: none; }
  .label:last-child { break-after: auto; page-break-after: auto; }
  .qr { width: 26mm; height: 26mm; flex: 0 0 26mm; display: flex; align-items: center; justify-content: center; }
  .qr svg { display: block; flex-shrink: 0; shape-rendering: crispEdges; }
  .label-text { width: 19mm; height: 26mm; flex: 0 0 19mm; display: flex; flex-direction: column; justify-content: center; gap: 2mm; }
  .company { font: bold 8pt/1.2 Arial, sans-serif; overflow-wrap: anywhere; flex-shrink: 0; }
  .tracking { color: #000; font: bold 6pt/1.25 Arial, sans-serif; white-space: normal; overflow-wrap: anywhere; flex-shrink: 0; }
  .credit { font: 5pt/1.2 Arial, sans-serif; color: #000; flex-shrink: 0; }
  @media screen { .label { margin-bottom: 12px; box-shadow: 0 2px 12px #0002; } }
  @media print {
    html, body { width: 50mm; margin: 0 !important; padding: 0 !important; background: #fff; }
    .labels { margin: 0; padding: 0; width: 50mm; display: block; }
    /* Leave a sub-millimetre allowance for browser page-size rounding.
       Reduced padding preserves the existing QR and text dimensions. */
    .label { width: 49.8mm; height: 29.8mm; padding: 1.9mm; margin: 0; box-shadow: none; break-after: auto; page-break-after: auto; }
    .label + .label { break-before: page; page-break-before: always; }
  }
`;

export async function openQRLabelPrint(asset) {
  const label = prepareQRLabel(asset);
  // Open directly from the click so the browser permits the preview window.
  const preview = window.open("", "_blank", "width=780,height=720");
  if (!preview)
    throw new Error("Allow pop-ups for this site, then select Print QR again.");
  try {
    const doc = preview.document;
    doc.title = "QR label - " + label.trackingId;
    doc.body.textContent = "Preparing your label...";
    const svg = await QRCode.toString(label.url, {
      type: "svg",
      margin: 4,
      errorCorrectionLevel: "M",
      color: { dark: "#000000", light: "#ffffff" },
    });
    if (preview.closed) return;
    doc.body.textContent = "";
    mountQRLabel(doc, asset, label, svg);
    // Open the native print dialog as soon as the label is ready.
    // Copy count is selected in that dialog.
    if (doc.fonts?.ready) await doc.fonts.ready;
    if (preview.closed) return;
    preview.focus();
    await new Promise(resolve => preview.requestAnimationFrame(() =>
      preview.requestAnimationFrame(resolve)
    ));
    if (preview.closed) return;
    preview.addEventListener("afterprint", () => preview.close(), { once: true });
    preview.print();
  } catch (error) {
    if (!preview.closed) preview.close();
    throw error;
  }
}

export function mountQRLabel(doc, asset, label, svg) {
    const style = doc.createElement("style");
    style.textContent = labelPrintCSS;
    doc.head.appendChild(style);
    const element = (tag, className, text) => {
      const node = doc.createElement(tag);
      if (className) node.className = className;
      if (text !== undefined) node.textContent = text;
      return node;
    };
    const labels = element("main", "labels");
    doc.body.appendChild(labels);
    const sticker = element("article", "label");
    const qr = element("div", "qr");
    const parsed = new DOMParser().parseFromString(svg, "image/svg+xml");
    const svgNode = doc.importNode(parsed.documentElement, true);
    svgNode.setAttribute("width", label.qrMm + "mm");
    svgNode.setAttribute("height", label.qrMm + "mm");
    qr.appendChild(svgNode);
    sticker.appendChild(qr);
    const text = element("div", "label-text");
    const company = element(
      "div",
      "company",
      String(asset.company || "VELLA")
        .trim()
        .toUpperCase(),
    );
    const tracking = element("div", "tracking", label.trackingId);
    text.appendChild(company);
    text.appendChild(tracking);
    text.appendChild(element("div", "credit", "SCAN FOR ASSET DETAILS"));
    sticker.appendChild(text);
    labels.appendChild(sticker);
    // Wrap long IDs naturally. Only reduce text if multiple rows exceed label height.
    let textScale = 1;
    for (
      let attempt = 0;
      attempt < 30 &&
      text.clientHeight > 0 &&
      text.scrollHeight > text.clientHeight;
      attempt++
    ) {
      textScale *= 0.9;
      tracking.style.fontSize = 6 * textScale + "pt";
      company.style.fontSize = 8 * textScale + "pt";
      text.style.gap = "1mm";
    }

    return sticker;
}

export async function downloadQRLabel(asset) {
  const label = prepareQRLabel(asset);
  const frame = document.createElement("iframe");
  frame.title = "QR label image";
  frame.setAttribute("aria-hidden", "true");
  frame.tabIndex = -1;
  frame.style.cssText = "position:fixed;left:-10000px;top:0;width:400px;height:300px;border:0;pointer-events:none;";
  document.body.appendChild(frame);
  try {
    const doc = frame.contentDocument;
    if (!doc) throw new Error("Unable to prepare the label image.");
    const [svg, { default: html2canvas }] = await Promise.all([
      QRCode.toString(label.url, {
        type: "svg", margin: 4, errorCorrectionLevel: "M",
        color: { dark: "#000000", light: "#ffffff" },
      }),
      import("html2canvas"),
    ]);
    if (doc.fonts?.ready) await doc.fonts.ready;
    const sticker = mountQRLabel(doc, asset, label, svg);
    sticker.style.margin = "0";
    sticker.style.boxShadow = "none";
    await new Promise(resolve => requestAnimationFrame(resolve));
    // 24 pixels/mm gives a sharp 1200 x 720 PNG in the same 5:3 proportions.
    const bounds = sticker.getBoundingClientRect();
    const canvas = document.createElement("canvas");
    canvas.width = 1200;
    canvas.height = 720;
    await html2canvas(sticker, {
      canvas, scale: 1200 / bounds.width, width: bounds.width,
      height: bounds.height, backgroundColor: "#ffffff", logging: false,
    });
    const blob = await new Promise((resolve, reject) => canvas.toBlob(
      value => value ? resolve(value) : reject(new Error("Unable to save the QR image.")),
      "image/png"
    ));
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "QR_Code_" + label.trackingId.replace(/[^a-zA-Z0-9_-]/g, "_") + ".png";
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } finally {
    frame.remove();
  }
}
